import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import * as yup from "yup";

import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { NumberFormatValues, NumericFormat } from "react-number-format";

import { RootState } from "@store/store";
import { CategoryModel, CategoryParamModel, FinancialRuleModel } from "@interfaces/model";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { FinancialRuleModelEditionDialogProps } from "@interfaces/slices";
import {
    useLazyGetFinancialRulesModelQuery,
    useLazyGetModelCategoriesQuery,
    useLazyGetModelParamsByCategoryQuery,
    useLazyGetModelParamsQuery,
    usePostFinancialRuleModelMutation,
} from "@services/api/model";

type FormValues = {
    type: string;
    category_id: number;
    model_parameter_id: number;
    operator: string;
    value: number | undefined;
    parameterDataType: string;
    parameterCode: string;
};

export const AltaEdicionReglaModeloFinanciera = (props: FinancialRuleModelEditionDialogProps) => {
    const dispatch = useDispatch();

    const [error, setError] = useState<string>("");

    const [existingRules, setExistingRules] = useState<Array<FinancialRuleModel>>([]);
    const [categories, setCategories] = useState<Array<CategoryModel>>([]);
    const [parameters, setParameters] = useState<Array<CategoryParamModel>>([]);
    const [availableAbortiveRules, setAvailibleAbortiveRules] = useState<Array<CategoryParamModel>>([]);
    const [availableRevisionRules, setAvailibleRevisionRules] = useState<Array<CategoryParamModel>>([]);

    const [params, setParams] = useState<Array<CategoryParamModel>>([]);

    const [triggerGetModelParameters] = useLazyGetModelParamsQuery();
    const [triggerPostFinancialRule] = usePostFinancialRuleModelMutation();

    const [triggerGetCategories, getCategoriesResult] = useLazyGetModelCategoriesQuery();
    const [triggerGetCategoryParams, getCategoryParamsResult] = useLazyGetModelParamsByCategoryQuery();

    const [triggerGetFinancialRulesModel] = useLazyGetFinancialRulesModelQuery();

    const userSession = useSelector((state: RootState) => state.app.session.user);

    const formikInitialValues = {
        type: "",
        category_id: 0,
        model_parameter_id: 0,
        operator: "",
        value: undefined,
        parameterDataType: "",
        parameterCode: "",
    };

    const formik = useFormik<FormValues>({
        initialValues: formikInitialValues,
        validationSchema: yup.object().shape({
            type: yup.string().required("Campo requerido"),
            category_id: yup.number().required("Campo requerido"),
            model_parameter_id: yup.string().required("Campo requerido"),
            operator: yup.string().when("parameterDataType", (parameterDataType, schema) => {
                const isNumberType = ["INT", "FLOAT"].includes(parameterDataType[0]);
                return isNumberType ? schema.required("Campo requerido") : schema.notRequired();
            }),
            value: yup.number().when("parameterDataType", (parameterDataType, schema) => {
                const isNumberType = ["INT", "FLOAT"].includes(parameterDataType[0]);
                return isNumberType ? schema.required("Campo requerido") : schema.notRequired();
            }),
            parameterDataType: yup.string().required(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                const isNumberType = ["INT", "FLOAT"].includes(values.parameterDataType);

                await triggerPostFinancialRule({
                    type: values.type,
                    financial_company_id: props.financialCoId,
                    tax_systems_id: props.taxSystemId,
                    model_parameter_id: values.model_parameter_id,
                    operator: isNumberType ? values.operator : "",
                    value: isNumberType ? values.value?.toString() : "",
                    rule_expression: isNumberType ? `${values.parameterCode} ${values.operator} ${values.value}` : "",
                }).unwrap();

                formik.resetForm({
                    values: {
                        ...values,
                    },
                });
                props.onAcceptClick();
                dispatch(setDialogConfig());
            } catch (error: any) {
                console.log(error);
                if (error.status === 400) {
                    setError(error.data.errors.code[0]);
                    setTimeout(() => {
                        setError("");
                    }, 2500);
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleSectionChanged = async (event: SelectChangeEvent<string>) => {
        formik.handleChange(event);
        await formik.setFieldValue("category_id", 0, true);
        await formik.setFieldValue("model_parameter_id", 0, true);
    };

    const handleCategoryChanged = async (event: SelectChangeEvent<number>) => {
        formik.handleChange(event);

        await formik.setFieldValue("model_parameter_id", 0, true);

        setParameters(
            [...(formik.values.type === "abortives" ? availableAbortiveRules : availableRevisionRules)].filter(
                (item) => item.model_category_id === event.target.value,
            ),
        );
    };

    const handleParameterChanged = async (event: SelectChangeEvent<number>) => {
        formik.handleChange(event);

        await formik.setFieldValue(
            "parameterDataType",
            parameters.find((param) => param.id === event.target.value)?.data_type,
            true,
        );

        await formik.setFieldValue("parameterCode", parameters.find((param) => param.id === event.target.value)?.code);

        await formik.setFieldValue("operator", "", true);
        await formik.setFieldValue("value", undefined, true);
    };

    const loadInfo = async () => {
        let queryParams = new URLSearchParams([
            ["q", `financial_company_id:${props.financialCoId}`],
            ["q", `tax_systems_id:${props.taxSystemId}`],
        ]);
        const rules = await triggerGetFinancialRulesModel(queryParams.toString()).unwrap();

        const parameters = await triggerGetModelParameters("").unwrap();

        setAvailibleAbortiveRules(
            parameters.filter(
                (parameter) =>
                    rules
                        .filter((rule) => rule.type === "abortives")
                        .some((rule) => rule.model_parameter_id === parameter.id) === false,
            ),
        );
        setAvailibleRevisionRules(
            parameters.filter(
                (parameter) =>
                    rules
                        .filter((rule) => rule.type === "revisions")
                        .some((rule) => rule.model_parameter_id === parameter.id) === false,
            ),
        );

        // console.log(parameters.filter(parameter => {
        //     const abortiveRules = rules.filter(rule => rule.type === "abortive");
        //     const revisionRules = rules.filter(rule => rule.type === "revision");

        //     return abortiveRules.some(abortiveRule => abortiveRule.model_parameter_id === parameter.id) && revisionRules.some(revisionRule => revisionRule.model_parameter_id === parameter.id);
        // }));

        // const categories = await triggerGetCategories("").unwrap();
        // setCategories(categories);
    };

    useEffect(() => {
        console.log(formik.errors);
    }, [formik.errors]);

    useEffect(() => {
        if (props.type === "ADD") {
            loadInfo();
        }
    }, [props.type]);

    return (
        <Box margin={4} maxHeight={"60vh"} maxWidth={"350px"} minWidth={"250px"}>
            <Stack rowGap={2}>
                <Typography variant="body1" color="main3" fontWeight={600} textAlign={"center"}>
                    {props.type === "ADD" ? "Alta" : "Edición"} de regla financiera
                </Typography>
                <form onSubmit={formik.handleSubmit} noValidate>
                    <Grid container spacing={2} justifyContent={"space-between"}>
                        <FormControl size="small" fullWidth>
                            <InputLabel required>Sección</InputLabel>
                            <Select
                                name="type"
                                label="Sección"
                                value={formik.values.type}
                                onBlur={formik.handleBlur}
                                onChange={(event) => handleSectionChanged(event)}
                                sx={{ color: "#528CD6", fontWeight: 600 }}
                            >
                                <MenuItem key={"abortive_status"} value={"abortive_status"}>
                                    Abortivos
                                </MenuItem>
                                <MenuItem key={"review_status"} value={"review_status"}>
                                    Revisión
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <Grid container size={12} alignItems={"center"}>
                            <Grid size={"grow"}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel required>Categoría</InputLabel>
                                    <Select
                                        name="category_id"
                                        label="Categoría"
                                        disabled={getCategoriesResult.isFetching}
                                        value={formik.values.category_id === 0 ? "" : formik.values.category_id}
                                        onBlur={formik.handleBlur}
                                        onChange={(event) => handleCategoryChanged(event)}
                                        sx={{ color: "#528CD6", fontWeight: 600 }}
                                    >
                                        {Array.from(
                                            new Map(
                                                [
                                                    ...(formik.values.type === "abortive_status"
                                                        ? availableAbortiveRules
                                                        : availableRevisionRules),
                                                ]
                                                    .map((t) => t.model_category)
                                                    .map((item) => [item.id, item]),
                                            ).values(),
                                        )
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((category) => (
                                                <MenuItem key={category.id} value={category.id}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Collapse in={getCategoriesResult.isFetching} orientation="horizontal" unmountOnExit>
                                <CircularProgress size={20} />
                            </Collapse>
                        </Grid>
                        <Grid container size={12} alignItems={"center"}>
                            <Grid size={"grow"}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel required>Parámetro</InputLabel>
                                    <Select
                                        name="model_parameter_id"
                                        label="Parámetro"
                                        disabled={getCategoriesResult.isFetching || getCategoryParamsResult.isFetching}
                                        value={
                                            formik.values.model_parameter_id === 0
                                                ? ""
                                                : formik.values.model_parameter_id
                                        }
                                        onBlur={formik.handleBlur}
                                        onChange={handleParameterChanged}
                                        sx={{ color: "#528CD6", fontWeight: 600 }}
                                    >
                                        {parameters.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Collapse in={getCategoryParamsResult.isFetching} orientation="horizontal" unmountOnExit>
                                <CircularProgress size={20} />
                            </Collapse>
                        </Grid>
                        <Collapse
                            in={["INT", "FLOAT"].includes(formik.values.parameterDataType)}
                            sx={{ width: "100%" }}
                        >
                            <Grid size={12} container columnGap={2}>
                                <Grid size={5}>
                                    {["INT", "FLOAT"].includes(formik.values.parameterDataType) && (
                                        <FormControl size="small" fullWidth>
                                            <InputLabel required>Operador</InputLabel>
                                            <Select
                                                name="operator"
                                                label="Operador"
                                                value={formik.values.operator}
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                            >
                                                <MenuItem value="=">=</MenuItem>
                                                <MenuItem value=">=">{">="}</MenuItem>
                                                <MenuItem value="<=">{"<="}</MenuItem>
                                                <MenuItem value=">">{">"}</MenuItem>
                                                <MenuItem value="<=>">{"<"}</MenuItem>
                                                <MenuItem value="!=">{"!="}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                </Grid>
                                <Grid size={"grow"}>
                                    <FormControl fullWidth>
                                        <NumericFormat
                                            name="value"
                                            label="Valor"
                                            size="small"
                                            displayType="input"
                                            required
                                            sx={{
                                                "& .MuiInputBase-root": { color: "#528CD6", fontWeight: 600 },
                                            }}
                                            thousandSeparator
                                            customInput={TextField}
                                            autoComplete="off"
                                            value={formik.values.value ?? ""}
                                            onValueChange={async (event: NumberFormatValues) =>
                                                await formik.setFieldValue("value", event.floatValue, true)
                                            }
                                            onBlur={formik.handleBlur}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Collapse>
                    </Grid>
                    <Collapse in={error !== ""}>
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    </Collapse>

                    <Stack direction={"row"} justifyContent={"space-around"} mt={2}>
                        <Button
                            variant="blue_outlined"
                            onClick={() => dispatch(setDialogConfig())}
                            disabled={formik.isSubmitting}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={formik.isSubmitting || !formik.isValid}
                            loading={formik.isSubmitting}
                        >
                            Agregar
                        </Button>
                    </Stack>
                </form>
            </Stack>
        </Box>
    );
};
